import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorLike } from '@apollo/client';
import _get from 'lodash/get';
import {
  Attachment,
  Documentation,
  useFormTemplateUnlinkAllDocumentationsMutation,
  CompanyPlanUpgradeFeatureEnum,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import Table from '@lib/components/Table/Table';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';
import ListEmptyState from '@lib/components/ListEmptyState/ListEmptyState';
import FeatureLockState from '@lib/components/FeatureLockState';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import toast from '@lib/components/Toast/Toast';
import Trans from '@lib/components/Trans/Trans';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import getDocumentationFilePermissions from '@lib/utils/documentation/getDocumentationFilePermissions';
import DocumentVersionNotes from '@lib/components/DocumentationPreview/DocumentVersionNotes';
import { IconName } from '@lib/components/Modal/enums';
import { NavItem } from '@lib/components/Menu/Menu';
import { Column } from '@lib/components/Table/types';
import { PlanState } from '@lib/enums/plan';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import { useTabCountersContext } from '@lib/contexts/TabCountersContext';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import DocumentBulkEditModal from 'routes/Documentation/DocumentList/DocumentBulkEditModal/DocumentBulkEditModal';
import FormTemplateBuilderMainBlockView from './FormTemplateBuilderMainBlockView';
import getTemplateActions from './utils/getTemplateActions';

interface Props {
  bulkEditIds: string[];
  onBulkEditCompleted: () => void;
  closeDeleteModal: () => void;
  closeFileViewer: () => void;
  closeUnlinkModal: () => void;
  closeVersionNotes: () => void;
  closeBulkEditModal: () => void;
  collection: Documentation[];
  deleteLoading: boolean;
  error: ErrorLike | undefined;
  fileViewerDocument: Documentation | null;
  firstLoading: boolean;
  formTemplateData: Values;
  formTemplateId: string;
  formTemplateLoading: boolean;
  formTemplateVersionData: Values;
  isBulkEditModalOpen: boolean;
  isDeleteModalOpen: boolean;
  isUnlinkModalOpen: boolean;
  isViewLocked?: boolean;
  isViewOnly: boolean;
  loading: boolean;
  loadingState: boolean;
  onConfirmedDelete: () => void;
  onConfirmedUnlink: () => void;
  onCreateTaskHandler: () => void;
  onDuplicateHandler: () => void;
  onEditHandler: () => void;
  onFormTemplateArchiveHandler: () => void;
  openDestroyModal: () => void;
  refetchFormTemplate?: () => void;
  tableActionsForSelected: React.ReactNode[];
  tableActionsMenu: NavItem<Documentation>[];
  tableColumns: Column<Documentation>[];
  totalCount: number;
  unlinkLoading: boolean;
  useBulkSelectResult: UseBulkSelectResult<Documentation>;
  versionNotesDocument: Documentation | null;
}

export default function FormTemplateBuilderDocumentsView(props: Props) {
  const {
    bulkEditIds,
    onBulkEditCompleted,
    closeDeleteModal,
    closeFileViewer,
    closeUnlinkModal,
    closeVersionNotes,
    closeBulkEditModal,
    collection,
    deleteLoading,
    error,
    fileViewerDocument,
    firstLoading,
    formTemplateData,
    formTemplateId,
    formTemplateLoading,
    formTemplateVersionData,
    isBulkEditModalOpen,
    isDeleteModalOpen,
    isUnlinkModalOpen,
    isViewLocked,
    isViewOnly,
    loading,
    loadingState,
    onConfirmedDelete,
    onConfirmedUnlink,
    onCreateTaskHandler,
    onDuplicateHandler,
    onEditHandler,
    onFormTemplateArchiveHandler,
    openDestroyModal,
    refetchFormTemplate,
    tableActionsForSelected,
    tableActionsMenu,
    tableColumns,
    totalCount,
    unlinkLoading,
    useBulkSelectResult,
    versionNotesDocument,
  } = props;

  const { t } = useTranslation();
  const fileViewerPermissions =
    getDocumentationFilePermissions(fileViewerDocument);
  const { handleOpenPlanModal, isFullAdmin }: ContextProps = useOutletContext();
  const showLockedView = !!isViewLocked && !!isFullAdmin;
  const onUpgradePlan = useCallback(() => {
    handleOpenPlanModal(PlanState.Locked, [
      CompanyPlanUpgradeFeatureEnum.Documentation,
    ]);
  }, [handleOpenPlanModal]);

  const hasLinkedDocs = _get(
    formTemplateData,
    'hasLinkedDocumentations',
    false,
  ) as boolean;
  const [isUnlinkConfirmOpen, setUnlinkConfirmOpen] = useState(false);
  const { refetchCounters } = useTabCountersContext();

  const [formTemplateUnlinkAllDocumentations, { loading: unlinkAllLoading }] =
    useFormTemplateUnlinkAllDocumentationsMutation({
      onError: (mutationError) => showGraphQLErrors(mutationError, t),
    });

  const onCloseUnlinkAllConfirm = useCallback(() => {
    setUnlinkConfirmOpen(false);
  }, []);

  const onConfirmUnlinkAll = useCallback(async () => {
    await formTemplateUnlinkAllDocumentations({
      variables: { input: { id: formTemplateId } },
    });
    await refetchFormTemplate?.();
    // The tab counter is fed by a separate linked-documents query that the
    // form-template refetch above leaves untouched.
    refetchCounters();
    toast({ content: t('unlink-documents-from-form-template-success') });
    setUnlinkConfirmOpen(false);
  }, [
    formTemplateId,
    formTemplateUnlinkAllDocumentations,
    refetchCounters,
    refetchFormTemplate,
    t,
  ]);

  const actions = getTemplateActions({
    onDuplicateHandler,
    onEditHandler: _get(formTemplateData, 'canUpdate', false)
      ? onEditHandler
      : undefined,
    openDestroyModal: _get(formTemplateData, 'canDestroy', false)
      ? openDestroyModal
      : undefined,
    onArchiveHandler: _get(formTemplateData, 'canArchive', false)
      ? onFormTemplateArchiveHandler
      : undefined,
    t,
  });

  const emptyState = (
    <ListEmptyState
      title={t('no-documents-linked-yet')}
      text={t('no-documents-linked-to-form-template-text')}
      actions={
        isViewOnly
          ? []
          : [
              <Button
                key="attach"
                buttonText={t('attach-document')}
                buttonType={ButtonTypes.primaryFilled}
                onClick={onEditHandler}
              />,
            ]
      }
    />
  );

  if (error) {
    return (
      <ErrorPage
        status={getStatusFromError(error)}
        description={getDescriptionFromError(error)}
      />
    );
  }

  return (
    <>
      <DashboardContentMaxWidth>
        {formTemplateLoading ? (
          <CircleLoader />
        ) : (
          <>
            <FormTemplateBuilderMainBlockView
              actions={actions}
              formTemplateData={formTemplateData}
              formTemplateId={formTemplateId}
              formTemplateVersionData={formTemplateVersionData}
              loadingState={loadingState}
              onCreateTaskHandler={onCreateTaskHandler}
              onDuplicateHandler={onDuplicateHandler}
              isViewOnly={isViewOnly}
            />
            {showLockedView && hasLinkedDocs ? (
              <SupportiveMessage
                text={t('advanced-documentation-features-not-in-plan')}
                color={SupportiveMessageColor.Yellow}
                type={SupportiveMessageType.FilledWithLeftBorder}
                actions={
                  <>
                    <Button
                      buttonText={t('unlink-document')}
                      buttonType={ButtonTypes.primaryText}
                      onClick={() => setUnlinkConfirmOpen(true)}
                    />
                    <Button
                      buttonText={t('upgrade-plan')}
                      buttonType={ButtonTypes.primaryFilled}
                      onClick={onUpgradePlan}
                    />
                  </>
                }
              />
            ) : null}
            {showLockedView && !hasLinkedDocs ? (
              <FeatureLockState
                title={t('unlock-documentation-in-forms')}
                text={<Trans i18nKey="unlock-documentation-in-forms-text" />}
                onUpgrade={onUpgradePlan}
              />
            ) : null}
            {showLockedView ? null : (
              <Table
                actionsForSelected={isViewOnly ? [] : tableActionsForSelected}
                actionsMenu={isViewOnly ? undefined : tableActionsMenu}
                columns={tableColumns}
                data={collection}
                emptyState={emptyState}
                id="form-template-documentations"
                loading={firstLoading || loading}
                totalEntries={totalCount}
                useBulkSelectResult={
                  isViewOnly ? undefined : useBulkSelectResult
                }
              />
            )}
          </>
        )}
      </DashboardContentMaxWidth>
      <DocumentBulkEditModal
        ids={bulkEditIds}
        isOpen={isBulkEditModalOpen}
        onClose={closeBulkEditModal}
        onCompleted={onBulkEditCompleted}
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-unlink')}
        description={
          <Trans i18nKey="unlink-documents-from-this-form-template-text" />
        }
        icon={IconName.LinkOff}
        isOpen={isUnlinkConfirmOpen}
        loading={unlinkAllLoading}
        onClose={onCloseUnlinkAllConfirm}
        onConfirm={onConfirmUnlinkAll}
        title={t('unlink-documents-from-this-form-template')}
        useProgress
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="documents-bulk-delete-text" />}
        icon={IconName.Delete}
        isOpen={isDeleteModalOpen}
        loading={deleteLoading}
        onClose={closeDeleteModal}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
        useProgress
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-unlink')}
        description={<Trans i18nKey="unlink-from-form-template-confirm-text" />}
        icon={IconName.Delete}
        isOpen={isUnlinkModalOpen}
        loading={unlinkLoading}
        onClose={closeUnlinkModal}
        onConfirm={onConfirmedUnlink}
        title={t('are-you-sure')}
        useProgress
      />
      <FileViewerModal
        attachment={
          _get(fileViewerDocument, [
            'currentVersion',
            'attachmentAttached',
          ]) as Attachment
        }
        attachmentName={fileViewerDocument?.name}
        documentationVersionId={fileViewerDocument?.currentVersion?.id}
        isOpen={!!fileViewerDocument}
        onClose={closeFileViewer}
        useDownloadTool={fileViewerPermissions.allowDownload}
        usePrintTool={fileViewerPermissions.allowPrint}
      />
      <DocumentVersionNotes
        isOpen={!!versionNotesDocument}
        notes={_get(versionNotesDocument, ['currentVersion', 'notes']) || ''}
        onClose={closeVersionNotes}
      />
    </>
  );
}
